// ── ConsensusChart — Unabated consensus fair-price chart ───────────
//
// Lightweight-charts line series rendering the Unabated consensus
// fair-probability for a single canonical game over time. Binary sports
// draw two lines that always sum to 100¢ (perfect mirror around the
// midline); soccer 3-way adds a Draw line and the three sum to 100¢
// instead — see HYDRA_API "Unabated Odds Stream" for the math. 3-way
// line colors are RANK-based (highest green / lowest blue / middle or
// tie grey — teamColors.rankColor) so they match the Odds column and
// the title's green-leader/blue-trailer convention.
//
// Design:
//   • Y axis hard-pinned 0-100¢ via each series'
//     `autoscaleInfoProvider` returning { minValue: 0, maxValue: 100 }.
//     Replaces an earlier sentinel-point hack that placed two fake
//     samples at Unix t=1 (1970) — when lightweight-charts auto-fit the
//     time axis, real 2026 data was compressed into the right-edge
//     pixel and the chart looked empty.
//   • Pill toggle ABOVE the chart: LIVE (auto-scroll, default) vs From
//     open (fitContent over the full points buffer). Both ranges sourced
//     from the same `consensusPoints` array; only the time-scale view
//     differs.
//   • Right-edge labels are absolutely-positioned <span>s whose `top`
//     pixel is computed from the latest fair-prob value via the chart's
//     priceToCoordinate API. Leader naturally lands in the upper half;
//     no special transition needed on a lead flip.
//   • Top-right badge: "Unabated · N books" where N is the latest
//     `contributingBooks`. Replaces the per-source Kalshi/Poly chips on
//     the equivalent Kalshi screenshot — consensus is multi-book, not
//     single-source.
//
// Sourcing assumption: caller hands us `consensusPoints` already
// Full-Game filtered and time-ascending (see useOddsStream). Each new
// consensus event appends one point; LIVE mode auto-scrolls to keep the
// right edge in view, From open mode disables auto-scroll and refits.

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ColorType,
  CrosshairMode,
  LineSeries,
  LineStyle,
  TickMarkType,
  createChart,
  type IChartApi,
  type ISeriesApi,
  type Time,
} from 'lightweight-charts';
import type {
  OddsStreamConsensus,
  OddsStreamConsensusHistoryPoint,
  OddsStreamGameState,
} from '../../services/oddsStream';
import { MIDDLE_COLOR, colorForSide, deriveWinnerSide, rankColor } from './teamColors';

const PINNED_PRICE_RANGE = { minValue: 0, maxValue: 100 } as const;

type ChartRange = 'live' | 'from-open';

interface Props {
  consensusPoints: OddsStreamConsensusHistoryPoint[];
  /** Latest Full-Game consensus row, used for the right-edge labels and
   *  the "Unabated · N books" badge. */
  latestConsensus: OddsStreamConsensus | null;
  /** Drives the green/blue color flip when the leader changes (and
   *  resolves final via score). */
  gameState: OddsStreamGameState | null;
  awayTeam: string;
  homeTeam: string;
  /** When provided, the "Unabated · N books" badge becomes a Link to
   *  this path (the per-source movement page). Omit on surfaces that
   *  shouldn't deep-link (e.g. embeds). */
  sourcesHref?: string | null;
}

export function ConsensusChart({
  consensusPoints,
  latestConsensus,
  gameState,
  awayTeam,
  homeTeam,
  sourcesHref,
}: Props) {
  // Binary: winner = green, loser = blue — flips whenever the consensus
  // crosses 50¢ (or a final score resolves the tie), falling back to
  // home-green / away-blue during the pre-snapshot connect window.
  // 3-way (drawFairProbCents present): rank colors across all three lines —
  // highest green, lowest blue, middle/tie grey.
  const drawCents = latestConsensus?.drawFairProbCents ?? null;
  const rankAll =
    latestConsensus != null && drawCents != null
      ? [latestConsensus.awayFairProbCents, latestConsensus.homeFairProbCents, drawCents]
      : null;
  const winnerSide = deriveWinnerSide(latestConsensus, gameState);
  const awayColor =
    rankAll && latestConsensus
      ? rankColor(latestConsensus.awayFairProbCents, rankAll)
      : colorForSide('away', winnerSide);
  const homeColor =
    rankAll && latestConsensus
      ? rankColor(latestConsensus.homeFairProbCents, rankAll)
      : colorForSide('home', winnerSide);
  const drawColor = rankAll && drawCents != null ? rankColor(drawCents, rankAll) : MIDDLE_COLOR;
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const awaySeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const homeSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const drawSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);

  const [range, setRange] = useState<ChartRange>('live');
  const [awayLabelY, setAwayLabelY] = useState<number | null>(null);
  const [homeLabelY, setHomeLabelY] = useState<number | null>(null);
  const [drawLabelY, setDrawLabelY] = useState<number | null>(null);

  // Build chart on mount; tear down on unmount. Resize is handled by a
  // ResizeObserver inside the effect so layout flips don't require a
  // remount.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const chart = createChart(el, {
      height: 420,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: '#9ca3af',
        fontFamily: 'ui-monospace, SFMono-Regular, monospace',
        fontSize: 11,
      },
      grid: {
        vertLines: { color: 'rgba(148,163,184,0.08)' },
        horzLines: { color: 'rgba(148,163,184,0.08)' },
      },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: {
        borderVisible: false,
        // Keep autoScale ON — the series-level autoscaleInfoProvider
        // only fires AS PART OF the auto-scale algorithm. With it off,
        // our { 0, 100 } range never gets applied and the axis renders
        // empty. The provider returns a fixed range so the scale
        // doesn't actually compress on close-to-50/50 data.
        autoScale: true,
        scaleMargins: { top: 0.05, bottom: 0.05 },
      },
      timeScale: {
        borderVisible: false,
        timeVisible: true,
        secondsVisible: false,
        tickMarkFormatter: (time: Time, tickType: TickMarkType) => {
          const ms = (time as number) * 1000;
          const d = new Date(ms);
          if (tickType === TickMarkType.Year || tickType === TickMarkType.Month) {
            return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
          }
          return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
        },
      },
    });
    chartRef.current = chart;

    const away = chart.addSeries(LineSeries, {
      // Initial color — re-applied via a separate effect when winnerSide
      // changes. Hard-coding the away default here keeps the line painted
      // before the first state update lands.
      color: awayColor,
      lineWidth: 2,
      lastValueVisible: false,
      priceLineVisible: false,
      lineStyle: LineStyle.Solid,
      // Force the price axis to always span 0..100 regardless of the
      // current value range. Without this the chart would zoom in
      // tightly when both lines hug 50/50 — the prompt explicitly asked
      // for a fixed scale.
      autoscaleInfoProvider: () => ({ priceRange: PINNED_PRICE_RANGE }),
    });
    const home = chart.addSeries(LineSeries, {
      color: homeColor,
      lineWidth: 2,
      lastValueVisible: false,
      priceLineVisible: false,
      lineStyle: LineStyle.Solid,
      autoscaleInfoProvider: () => ({ priceRange: PINNED_PRICE_RANGE }),
    });
    // Soccer 3-way Draw — created unconditionally (binary games just never
    // feed it data, which is free). Initial grey; the color effect below
    // re-applies the live rank color alongside the team lines.
    const draw = chart.addSeries(LineSeries, {
      color: MIDDLE_COLOR,
      lineWidth: 2,
      lastValueVisible: false,
      priceLineVisible: false,
      lineStyle: LineStyle.Solid,
      autoscaleInfoProvider: () => ({ priceRange: PINNED_PRICE_RANGE }),
    });

    awaySeriesRef.current = away;
    homeSeriesRef.current = home;
    drawSeriesRef.current = draw;

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        chart.applyOptions({ width, height });
      }
    });
    ro.observe(el);

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
      awaySeriesRef.current = null;
      homeSeriesRef.current = null;
      drawSeriesRef.current = null;
    };
  }, []);

  // Re-apply line colors when the winner/rank flips. lightweight-charts'
  // applyOptions is cheap; this runs only on actual color changes.
  useEffect(() => {
    awaySeriesRef.current?.applyOptions({ color: awayColor });
    homeSeriesRef.current?.applyOptions({ color: homeColor });
    drawSeriesRef.current?.applyOptions({ color: drawColor });
  }, [awayColor, homeColor, drawColor]);

  // Push consensus points to the two series. setData is the canonical
  // write — the lib handles incremental update internally. Note:
  // lightweight-charts requires strictly-ascending unique times. The
  // consensus history can have multiple points within a single second
  // (we saw 3 in 1779413078), so we dedup at second-resolution by
  // collapsing into a Map keyed on floor(ts/1000); the latest point
  // for any given second wins. Without this the lib silently rejects
  // the whole setData call and the chart paints empty.
  useEffect(() => {
    const away = awaySeriesRef.current;
    const home = homeSeriesRef.current;
    const draw = drawSeriesRef.current;
    if (!away || !home || !draw) return;

    const bySecond = new Map<number, OddsStreamConsensusHistoryPoint>();
    for (const p of consensusPoints) {
      const t = Math.floor(p.ts / 1000);
      bySecond.set(t, p); // last-write-wins within a second
    }
    const seconds = [...bySecond.keys()].sort((a, b) => a - b);
    const awayData: Array<{ time: Time; value: number }> = [];
    const homeData: Array<{ time: Time; value: number }> = [];
    const drawData: Array<{ time: Time; value: number }> = [];
    for (const t of seconds) {
      const p = bySecond.get(t)!;
      awayData.push({ time: t as Time, value: p.awayFairProbCents });
      homeData.push({ time: t as Time, value: p.homeFairProbCents });
      if (p.drawFairProbCents != null) {
        drawData.push({ time: t as Time, value: p.drawFairProbCents });
      }
    }

    try {
      away.setData(awayData);
      home.setData(homeData);
      draw.setData(drawData); // empty on binary sports — clears cleanly
    } catch (err) {
      // Surface anything the lib rejects (bad time order, duplicates)
      // so we don't silently render empty again.
      // eslint-disable-next-line no-console
      console.error('[ConsensusChart] setData failed', err, {
        awayCount: awayData.length,
        homeCount: homeData.length,
        firstAway: awayData[0],
        lastAway: awayData[awayData.length - 1],
      });
    }
  }, [consensusPoints]);

  // Range mode wires to time-scale behavior.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    if (consensusPoints.length === 0) return;

    if (range === 'live') {
      chart.timeScale().scrollToRealTime();
    } else {
      const earliest = consensusPoints[0]?.ts;
      const latest = consensusPoints[consensusPoints.length - 1]?.ts;
      if (earliest != null && latest != null && earliest < latest) {
        chart.timeScale().setVisibleRange({
          from: Math.floor(earliest / 1000) as Time,
          to: Math.floor(latest / 1000) as Time,
        });
      } else {
        chart.timeScale().fitContent();
      }
    }
  }, [range, consensusPoints]);

  // Compute right-edge label Y positions from the latest fair-prob
  // values. Re-runs whenever the latest consensus changes OR the chart
  // resizes (so labels stay glued to their values).
  useLayoutEffect(() => {
    const away = awaySeriesRef.current;
    const home = homeSeriesRef.current;
    const draw = drawSeriesRef.current;
    if (!away || !home || !latestConsensus) {
      setAwayLabelY(null);
      setHomeLabelY(null);
      setDrawLabelY(null);
      return;
    }
    const ay = away.priceToCoordinate(latestConsensus.awayFairProbCents);
    const hy = home.priceToCoordinate(latestConsensus.homeFairProbCents);
    setAwayLabelY(typeof ay === 'number' ? ay : null);
    setHomeLabelY(typeof hy === 'number' ? hy : null);
    const drawCents = latestConsensus.drawFairProbCents;
    if (draw && drawCents != null) {
      const dy = draw.priceToCoordinate(drawCents);
      setDrawLabelY(typeof dy === 'number' ? dy : null);
    } else {
      setDrawLabelY(null);
    }
  }, [latestConsensus, consensusPoints]);

  const awayLabel = latestConsensus
    ? `${awayTeam} ${latestConsensus.awayFairProbCents}%`
    : awayTeam;
  const homeLabel = latestConsensus
    ? `${homeTeam} ${latestConsensus.homeFairProbCents}%`
    : homeTeam;
  const drawLabel =
    latestConsensus?.drawFairProbCents != null
      ? `Draw ${latestConsensus.drawFairProbCents}%`
      : null;

  return (
    // No card chrome — the parent (GameDetail) wraps this in its own card
    // at the bottom of the live column (tables first, chart last, per
    // trader feedback Jun 6). The toolbar row keeps only a bottom border;
    // the card edge above it provides the top separation.
    <div>
      <div className="border-border flex items-center justify-between border-b px-3 py-2">
        <div className="flex items-center gap-1.5">
          <RangePill label="LIVE" active={range === 'live'} onClick={() => setRange('live')} />
          <RangePill label="From open" active={range === 'from-open'} onClick={() => setRange('from-open')} />
        </div>
        {sourcesHref ? (
          <Link
            to={sourcesHref}
            className="text-muted-foreground hover:text-foreground font-mono text-[11px] hover:underline"
            title="See per-source line movement"
          >
            Unabated · {latestConsensus?.contributingBooks ?? 0} books
          </Link>
        ) : (
          <span
            className="text-muted-foreground font-mono text-[11px]"
            title="De-vigged consensus across contributing sportsbooks"
          >
            Unabated · {latestConsensus?.contributingBooks ?? 0} books
          </span>
        )}
      </div>
      <div className="relative">
        <div ref={containerRef} className="h-[420px] w-full" />
        {awayLabelY !== null && latestConsensus && (
          <span
            className="pointer-events-none absolute right-[60px] text-[11px] font-semibold"
            style={{
              top: awayLabelY,
              transform: labelAbove(latestConsensus.awayFairProbCents)
                ? 'translateY(calc(-100% - 6px))'
                : 'translateY(6px)',
              color: awayColor,
            }}
          >
            {awayLabel}
          </span>
        )}
        {homeLabelY !== null && latestConsensus && (
          <span
            className="pointer-events-none absolute right-[60px] text-[11px] font-semibold"
            style={{
              top: homeLabelY,
              transform: labelAbove(latestConsensus.homeFairProbCents)
                ? 'translateY(calc(-100% - 6px))'
                : 'translateY(6px)',
              color: homeColor,
            }}
          >
            {homeLabel}
          </span>
        )}
        {drawLabelY !== null && drawLabel != null && latestConsensus?.drawFairProbCents != null && (
          <span
            className="pointer-events-none absolute right-[60px] text-[11px] font-semibold"
            style={{
              top: drawLabelY,
              transform: labelAbove(latestConsensus.drawFairProbCents)
                ? 'translateY(calc(-100% - 6px))'
                : 'translateY(6px)',
              color: drawColor,
            }}
          >
            {drawLabel}
          </span>
        )}
        {/* Empty-state overlay — when hydra has the game state but no
            Unabated lines/consensus yet, the chart paints blank. Tell the
            user so it doesn't look like a g1 bug. */}
        {consensusPoints.length === 0 && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="bg-card/85 text-muted-foreground rounded border border-border/40 px-3 py-2 text-center text-[11px] leading-snug">
              <div className="text-foreground font-semibold">
                No Unabated consensus yet
              </div>
              <div>Hydra has this game's state but no sportsbook lines.</div>
              <div className="opacity-70">Chart will populate once lines arrive.</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Where to render the right-edge label relative to its line, by value
// quartile. Avoids overlap with the line itself AND keeps the label
// from clipping the chart's top/bottom edges:
//
//   75 - 100 → below the line (line is near top, push label down)
//   50 - 75  → above the line (away from the midline)
//   25 - 50  → below the line (away from the midline)
//   0  - 25  → above the line (line is near bottom, push label up)
//
// The two labels naturally repel each other (when one team is in
// 50-75, the other is in 25-50 — one above, one below).
function labelAbove(cents: number): boolean {
  if (cents >= 75) return false;
  if (cents >= 50) return true;
  if (cents >= 25) return false;
  return true;
}

function RangePill({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors ${
        active
          ? 'bg-blue-500/20 text-blue-400 ring-1 ring-blue-500/40'
          : 'bg-muted text-muted-foreground hover:text-foreground'
      }`}
    >
      {label}
    </button>
  );
}
